import { useCallback } from 'react'
import { festivalWindow } from '@shared/dates'
import { getNow } from '../lib/clock'
import { isEventLive } from '../lib/formatDate'
import { track } from '../lib/analytics'
import { useTransitionNavigate } from '../lib/viewTransition'
import type { Event } from '../types'
import type { EventInterestStatus } from './useUserEventInterests'

/** Router state festival mode reads to announce an automatic entry. */
/** The fields opening an event reads — a search hit satisfies it as well as a full Event. */
export type OpenableEvent = Pick<Event, 'id' | 'type' | 'cancelled' | 'startDate' | 'endDate' | 'firstSetDay' | 'lastSetDay'>

export interface AutoEnteredState {
  festivalAutoEntered: true
}

// Tapping a live festival you're interested in or going to opens festival mode
// directly — it's what you want on site, and the promo dialog that used to ask
// was dismissed far more often than it was answered. It lands on the schedule,
// the tab people spend their time on during a festival. Only card taps do this:
// deep links, shared links and Exit all land on the event page as addressed.
export function shouldAutoEnterFestival(
  event: OpenableEvent,
  status: EventInterestStatus | undefined,
  now: Date,
): boolean {
  return (
    event.type === 'festival' &&
    !event.cancelled &&
    (status === 'going' || status === 'interested') &&
    isEventLive(festivalWindow(event), now)
  )
}

/** The click handler for an event card. */
export function useOpenEvent() {
  const navigate = useTransitionNavigate()
  return useCallback(
    (event: OpenableEvent, status: EventInterestStatus | undefined) => {
      // getNow() honours the clock override, so a pinned debug clock triggers
      // auto-entry exactly as the real day would.
      if (shouldAutoEnterFestival(event, status, getNow())) {
        track('enter_festival_mode', { event_id: event.id, live: true, entry_point: 'auto' })
        const state: AutoEnteredState = { festivalAutoEntered: true }
        navigate(`/live-festival/${event.id}/schedule`, { state })
        return
      }
      navigate(`/events/${event.id}`)
    },
    [navigate],
  )
}
